<template>
  <div class="incentive-page">
    <section class="incentive-hero">
      <div>
        <div class="hero-eyebrow">RIDER REWARD OPERATIONS</div>
        <h1 class="hero-title">Order Incentive Approval</h1>
        <p class="hero-subtitle">
          ตรวจสอบและอนุมัติเครดิตล็อตสุดท้ายให้ Rider อย่างปลอดภัย
        </p>
      </div>
    </section>

    <v-card class="incentive-shell glass-card">
      <v-toolbar flat color="transparent" class="incentive-toolbar">
        <div class="toolbar-copy">
          <div class="toolbar-title">Approval queue</div>
          <div class="toolbar-subtitle">
            {{ totalQueueCount }} รายการรอตรวจสอบและจ่าย
          </div>
        </div>
        <v-spacer />
        <v-btn
          icon
          color="primary"
          class="black--text"
          :loading="loading"
          @click="loadQueue"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="filter-panel">
        <v-layout row wrap align-center>
          <v-flex xs12 sm6 md4>
            <v-select
              v-model="provinceFilter"
              :items="provinceOptions"
              item-text="text"
              item-value="value"
              label="Filter by province"
              prepend-icon="place"
              outline
              hide-details
            />
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-layout row wrap class="summary-row">
        <v-flex
          v-for="item in summaryCards"
          :key="item.label"
          xs12
          sm6
          class="pa-1"
        >
          <div class="summary-item">
            <v-icon :color="item.color">{{ item.icon }}</v-icon>
            <div>
              <div class="summary-value">{{ item.value }}</div>
              <div class="summary-label">{{ item.label }}</div>
            </div>
          </div>
        </v-flex>
      </v-layout>

      <section
        v-for="section in queueSections"
        :key="section.key"
        class="queue-section"
      >
        <div class="queue-section__header">
          <div>
            <div class="queue-section__title">
              <v-icon :color="section.color" small>{{ section.icon }}</v-icon>
              {{ section.title }}
            </div>
            <div class="queue-section__subtitle">{{ section.subtitle }}</div>
          </div>
          <v-chip small :color="section.color" text-color="white">
            {{ section.items.length }} รายการ
          </v-chip>
        </div>

        <v-data-table
          :headers="headers"
          :items="section.items"
          :loading="loading"
          class="incentive-table"
        >
          <template slot="items" slot-scope="{ item }">
            <td>
              <div class="strong-text">{{ item.order_code }}</div>
              <div class="muted-text">ID {{ item.order_id }}</div>
            </td>
            <td>
              <v-chip small outline color="blue-grey">
                {{ item.province }}
              </v-chip>
            </td>
            <td>
              <v-chip
                small
                :color="serviceColor(item.order_type)"
                text-color="white"
              >
                {{ serviceName(item.order_type) }}
              </v-chip>
            </td>
            <td>
              <div class="strong-text">
                {{ item.final_rider_name || `Rider ${item.final_rider_id}` }}
              </div>
              <div class="muted-text">ID {{ item.final_rider_id || "-" }}</div>
            </td>
            <td class="text-xs-right">
              <div>
                <s v-if="item.last_adjustment" class="previous-amount">
                  {{ money(item.last_adjustment.old_amount) }} บาท
                </s>
                <span class="amount-text">{{ money(item.amount) }} บาท</span>
              </div>
              <div v-if="item.last_adjustment" class="muted-text">
                {{ adjustmentActor(item.last_adjustment) }} ·
                {{ displayDateTime(item.last_adjustment.created_at) }}
              </div>
            </td>
            <td>{{ displayDate(item.eligible_on) }}</td>
            <td class="text-xs-center">
              <order-incentive-dialog
                :order-id="item.order_id"
                :order-code="item.order_code"
                :province="item.province"
                :order-status="6"
                :value="item"
                class="approval-edit-action"
                @saved="handleAdjustment(item, $event)"
                :isEdit="true"
                :canCancel="true"
              />
              <v-btn
                round
                :color="section.key === 'tomorrow' ? 'warning' : 'success'"
                class="white--text"
                :loading="approvingId === item.id"
                @click="openApproval(item, section.key)"
                small
              >
                <v-icon left small>check_circle</v-icon>
                อนุมัติ
              </v-btn>
            </td>
          </template>
          <template slot="no-data">
            <div v-if="loading" class="queue-loading-state">
              <v-progress-circular
                indeterminate
                color="primary"
                :size="28"
                :width="3"
              />
              <span>กำลังโหลดคิว...</span>
            </div>
            <div v-else-if="loadFailed" class="empty-state">
              <v-icon color="error" size="42">error_outline</v-icon>
              <div class="empty-state__title">โหลดคิวไม่สำเร็จ</div>
              <div class="muted-text">กดปุ่มรีเฟรชเพื่อลองอีกครั้ง</div>
            </div>
            <div v-else-if="hasLoaded" class="empty-state">
              <v-icon :color="section.color" size="42">
                check_circle_outline
              </v-icon>
              <div class="empty-state__title">{{ section.emptyTitle }}</div>
              <div class="muted-text">{{ section.emptySubtitle }}</div>
            </div>
          </template>
        </v-data-table>
      </section>
    </v-card>

    <v-dialog
      v-model="approvalDialog"
      max-width="580"
      content-class="approval-dialog"
    >
      <v-card class="dialog-glass-card">
        <v-toolbar dark color="rgba(24, 28, 36, .94)" class="dialog-toolbar">
          <v-toolbar-title>ยืนยันการจ่าย Order Incentive</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="approvalDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text v-if="selectedItem" class="approval-dialog__content">
          <div class="approval-highlight">
            <div class="approval-highlight__icon"><v-icon>stars</v-icon></div>
            <div>
              <div class="dialog-label">PAYOUT AMOUNT</div>
              <div class="approval-amount">
                {{ money(selectedItem.amount) }} บาท
              </div>
              <div class="muted-text">Order #{{ selectedItem.order_code }}</div>
            </div>
          </div>
          <div class="approval-details">
            <div>
              <span>Rider</span>
              <strong>
                {{
                  selectedItem.final_rider_name || selectedItem.final_rider_id
                }}
              </strong>
            </div>
            <div>
              <span>Service</span>
              <strong>{{ serviceName(selectedItem.order_type) }}</strong>
            </div>
            <div>
              <span>Province</span>
              <strong>{{ selectedItem.province }}</strong>
            </div>
          </div>
          <v-alert type="info" :value="true" class="mt-4">
            ระบบจะบันทึก Wallet transaction เพียงหนึ่งครั้งต่อออเดอร์
          </v-alert>
          <v-alert v-if="isEarlyApproval" type="warning" :value="true">
            รายการนี้อยู่ในคิวพรุ่งนี้ การยืนยันจะจ่ายเครดิตเพิ่มเติมให้ Rider
            ทันทีโดยไม่รอถึงวันที่
            {{ displayDate(selectedItem.eligible_on) }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn round flat @click="approvalDialog = false">ยังก่อน</v-btn>
          <v-btn
            round
            color="success"
            class="white--text"
            :loading="approvingId === (selectedItem && selectedItem.id)"
            @click="approve"
          >
            <v-icon left small>account_balance_wallet</v-icon>
            ยืนยันการจ่าย
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="messageColor">
      {{ message }}
      <v-btn flat @click="snackbar = false">ปิด</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { globalAxios } from "@/axios-path.js";
import OrderIncentiveDialog from "@/components/back-office/OrderOnline/OrderIncentiveDialog.vue";

export default {
  components: {
    OrderIncentiveDialog,
  },
  data() {
    return {
      loading: false,
      hasLoaded: false,
      loadFailed: false,
      approvingId: null,
      queue: {
        today: [],
        tomorrow: [],
      },
      approvalDialog: false,
      selectedItem: null,
      snackbar: false,
      message: "",
      messageColor: "success",
      provinceFilter: "",
      headers: [
        { text: "Order", value: "order_code" },
        { text: "Province", value: "province" },
        { text: "Service", value: "order_type" },
        { text: "Rider", value: "final_rider_name" },
        { text: "Amount", value: "amount", align: "right" },
        { text: "Eligible", value: "eligible_on" },
        { text: "Action", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    provinceOptions() {
      const provinces = [...this.queue.today, ...this.queue.tomorrow]
        .map((item) => String(item.province || "").trim())
        .filter(Boolean)
        .filter((province, index, values) => values.indexOf(province) === index)
        .sort();
      return [
        { text: "All provinces", value: "" },
        ...provinces.map((province) => ({
          text: province,
          value: province,
        })),
      ];
    },
    filteredQueue() {
      const filter = String(this.provinceFilter || "").trim();
      const matchesProvince = (item) =>
        !filter || String(item.province || "").trim() === filter;
      return {
        today: this.queue.today.filter(matchesProvince),
        tomorrow: this.queue.tomorrow.filter(matchesProvince),
      };
    },
    totalQueueCount() {
      return (
        this.filteredQueue.today.length + this.filteredQueue.tomorrow.length
      );
    },
    queueSections() {
      return [
        {
          key: "today",
          title: "Today queue",
          subtitle: "รายการของวันนี้และรายการค้างจากวันก่อน",
          color: "success",
          icon: "today",
          items: this.filteredQueue.today,
          emptyTitle: "ไม่มีคิววันนี้หรือคิวค้าง",
          emptySubtitle: "รายการที่ถึงกำหนดถูกจัดการครบแล้ว",
        },
        {
          key: "tomorrow",
          title: "Tomorrow queue",
          subtitle: "รายการรอวันพรุ่งนี้ สามารถอนุมัติล่วงหน้าได้ทันที",
          color: "warning",
          icon: "schedule",
          items: this.filteredQueue.tomorrow,
          emptyTitle: "ไม่มีคิวพรุ่งนี้",
          emptySubtitle: "ยังไม่มี Order Incentive ที่รอรอบถัดไป",
        },
      ];
    },
    isEarlyApproval() {
      return Boolean(
        this.selectedItem && this.selectedItem.queueGroup === "tomorrow"
      );
    },
    summaryCards() {
      return [
        {
          label: "Today & overdue",
          value: this.filteredQueue.today.length,
          icon: "today",
          color: "success",
        },
        {
          label: "Tomorrow",
          value: this.filteredQueue.tomorrow.length,
          icon: "schedule",
          color: "warning",
        },
      ];
    },
  },
  created() {
    this.loadQueue();
  },
  methods: {
    money(value) {
      return Number(value || 0).toLocaleString("th-TH");
    },
    serviceName(type) {
      return Number(type) === 2 ? "Messenger" : "Food";
    },
    serviceColor(type) {
      return Number(type) === 2 ? "blue-grey" : "deep-orange";
    },
    displayDate(value) {
      if (!value) return "-";
      return new Date(`${value}T00:00:00`).toLocaleDateString("th-TH");
    },
    displayDateTime(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("th-TH");
    },
    adjustmentActor(adjustment) {
      return adjustment.actor_user_name || `User ${adjustment.actor_user_id}`;
    },
    async handleAdjustment(item, incentive) {
      const action = incentive.status === "cancelled" ? "ยกเลิก" : "แก้ไข";
      this.notify(`${action} Incentive ออเดอร์ ${item.order_code} แล้ว`);
      await this.loadQueue();
    },
    notify(message, color = "success") {
      this.message = message;
      this.messageColor = color;
      this.snackbar = true;
    },
    async loadQueue() {
      this.loading = true;
      this.loadFailed = false;
      try {
        const response = await globalAxios.get(
          "/api/v1/order-incentives/approval-queue"
        );
        const data = response.data || {};
        if (Array.isArray(data)) {
          // Backward compatibility while an older backend version is still
          // serving the original single approval queue response.
          this.queue = {
            today: data,
            tomorrow: [],
          };
        } else {
          this.queue = {
            today: data.today || [],
            tomorrow: data.tomorrow || [],
          };
        }
        this.hasLoaded = true;
      } catch (error) {
        this.loadFailed = true;
        this.notify(
          error.response?.data?.detail || "โหลดคิวไม่สำเร็จ",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    openApproval(item, queueGroup) {
      this.selectedItem = { ...item, queueGroup };
      this.approvalDialog = true;
    },
    async approve() {
      if (!this.selectedItem) return;
      const item = this.selectedItem;
      this.approvingId = item.id;
      try {
        await globalAxios.post(`/api/v1/order-incentives/${item.id}/approve`, {
          approve_early: this.isEarlyApproval,
        });
        this.approvalDialog = false;
        this.selectedItem = null;
        this.notify(`จ่าย Order Incentive ออเดอร์ ${item.order_code} แล้ว`);
        await this.loadQueue();
      } catch (error) {
        this.notify(
          error.response?.data?.detail || "อนุมัติไม่สำเร็จ",
          "error"
        );
      } finally {
        this.approvingId = null;
      }
    },
  },
};
</script>

<style scoped>
.incentive-page {
  min-height: 100%;
  padding: 24px 16px 40px;
  position: relative;
}

.incentive-hero {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 auto 22px;
  max-width: 1400px;
  padding: 18px 10px;
  position: relative;
}

.hero-eyebrow,
.dialog-label {
  color: #9b8500;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.18em;
}

.hero-title {
  color: #263238;
  font-size: 42px;
  font-weight: 750;
  letter-spacing: -0.04em;
  line-height: 1.1;
  margin: 7px 0;
  text-shadow: 2px 3px 12px rgba(33, 33, 32, 0.25);
}

.hero-subtitle {
  color: #73808a;
  font-size: 16px;
  margin: 0;
}

.hero-icon {
  align-items: center;
  background: rgba(255, 255, 255, 0.52);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 22px;
  box-shadow: 0 18px 45px rgba(44, 62, 80, 0.11);
  display: flex;
  height: 82px;
  justify-content: center;
  width: 82px;
}

.hero-icon .v-icon {
  color: #d6b900;
  font-size: 38px;
}

.incentive-shell {
  border-radius: 16px;
  margin: 0 auto;
  max-width: 1400px;
  overflow: hidden;
  position: relative;
}

.glass-card {
  background: rgba(255, 255, 255, 0.68) !important;
  border: 1px solid rgba(255, 255, 255, 0.82) !important;
  box-shadow: 0 18px 52px rgba(27, 39, 51, 0.1) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.incentive-toolbar {
  min-height: 88px;
  padding: 0 18px;
}

.filter-panel {
  background: rgba(247, 249, 250, 0.82);
  border-bottom: 1px solid #e3e8eb;
  border-top: 1px solid #e3e8eb;
  padding: 16px 18px;
}

.toolbar-title {
  color: #263238;
  font-size: 20px;
  font-weight: 700;
}

.toolbar-subtitle,
.muted-text,
.summary-label {
  color: #777777;
  font-size: 12px;
}

.summary-row {
  padding: 8px 16px 16px;
}

.summary-item {
  align-items: center;
  background: rgba(255, 255, 255, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  box-shadow: 0 8px 22px rgba(35, 45, 55, 0.06);
  display: flex;
  gap: 12px;
  min-height: 74px;
  padding: 14px;
}

.summary-value {
  color: #222222;
  font-size: 22px;
  font-weight: 700;
}

.strong-text {
  color: #222222;
  font-weight: 600;
}

.amount-text {
  color: #1b5e20;
  font-size: 17px;
  font-weight: 750;
}

.previous-amount {
  color: #8b9297;
  display: inline-block;
  font-size: 12px;
  margin-right: 6px;
}

.approval-edit-action {
  display: inline-block;
  vertical-align: middle;
}

.queue-section {
  border-top: 1px solid rgba(210, 217, 222, 0.8);
  padding-top: 8px;
}

.queue-section + .queue-section {
  margin-top: 20px;
}

.queue-section__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 16px 20px 12px;
}

.queue-section__title {
  align-items: center;
  color: #263238;
  display: flex;
  font-size: 17px;
  font-weight: 750;
  gap: 8px;
}

.queue-section__subtitle {
  color: #777777;
  font-size: 12px;
  margin-top: 3px;
}

.incentive-table {
  border-top: 1px solid #eeeeee;
}

.incentive-table >>> table {
  min-width: 1040px;
}

.empty-state {
  padding: 34px 16px;
  text-align: center;
}

.queue-loading-state {
  align-items: center;
  color: #777777;
  display: flex;
  gap: 10px;
  justify-content: center;
  min-height: 116px;
}

.empty-state__title {
  color: #263238;
  font-size: 17px;
  font-weight: 700;
  margin: 8px 0 2px;
}

.dialog-glass-card {
  background: rgba(244, 247, 248, 0.94) !important;
  border: 1px solid rgba(255, 255, 255, 0.78) !important;
  border-radius: 20px !important;
  overflow: hidden;
}

.approval-dialog__content {
  padding: 24px;
}

.approval-highlight {
  align-items: center;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(210, 217, 222, 0.9);
  border-radius: 16px;
  display: flex;
  gap: 16px;
  padding: 18px;
}

.approval-highlight__icon {
  align-items: center;
  background: rgba(253, 223, 0, 0.2);
  border-radius: 16px;
  display: flex;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.approval-highlight__icon .v-icon {
  color: #b99f00;
  font-size: 30px;
}

.approval-amount {
  color: #1b5e20;
  font-size: 28px;
  font-weight: 750;
}

.approval-details {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 16px;
}

.approval-details > div {
  background: rgba(255, 255, 255, 0.68);
  border: 1px solid #e3e8eb;
  border-radius: 12px;
  padding: 12px;
}

.approval-details span,
.approval-details strong {
  display: block;
}

.approval-details span {
  color: #82909a;
  font-size: 11px;
}

.dialog-actions {
  background: rgba(255, 255, 255, 0.76);
  border-top: 1px solid rgba(210, 217, 222, 0.7);
  padding: 14px 20px;
}

@media (max-width: 600px) {
  .incentive-page {
    padding: 12px 6px 28px;
  }

  .hero-title {
    font-size: 30px;
  }

  .hero-icon {
    display: none;
  }

  .approval-details {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.incentive-page .v-btn,
.approval-dialog .v-btn {
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.incentive-page .v-btn:not(.v-btn--icon),
.approval-dialog .v-btn:not(.v-btn--icon) {
  border-radius: 24px;
  min-width: 104px;
  padding-left: 24px;
  padding-right: 24px;
  box-shadow: 0 8px 18px rgba(38, 50, 56, 0.18) !important;
  transform: translateY(-2px);
}

.approval-dialog {
  border-radius: 20px;
  box-shadow: 0 28px 90px rgba(15, 23, 32, 0.28);
  overflow: hidden;
}
</style>
